import type { ImagesListType } from 'react-spring-lightbox';

import { FileArchive, FileCsv, FilePdf, FileText, User } from '@liveauctioneers/hammer-ui-core/icons';
import { AnyIcon } from '@liveauctioneers/hammer-ui-core/icons/anyIcon';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useState, useMemo } from 'react';

import { longFormatDate } from '@/utils/dates';

import { getFileExtensionName, getFileName } from '../../../../utils/filePath';
import { MessageType } from '../../types/Message';
import { AttachmentLightbox } from '../AttachmentLightbox/AttachmentLightbox';

type Props = {
    readonly message: MessageType;
    readonly testid?: string;
};

const FileIcons = {
    csv: FileCsv,
    doc: FileText,
    docx: FileText,
    numbers: FileText,
    pages: FileText,
    pdf: FilePdf,
    xls: FileText,
    xlsx: FileText,
    zip: FileArchive,
};

interface FileAttachment {
    readonly extension: keyof typeof FileIcons;
    readonly filename: string;
    readonly icon: AnyIcon;
    readonly url: string;
}

const attachmentContainerClass = 'flex flex-row flex-wrap mb-xs [&_a]:no-underline [&>div:not(:last-child)]:mr-xs';

const Message = ({ message, testid = 'messengerMessage' }: Props) => {
    const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
    const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
    const { attachments = [], messageBody, messageId, sender, tsSent } = message;

    const [fileAttachments, lightboxImages] = useMemo(() => {
        const files: FileAttachment[] = [];
        const images: ImagesListType = [];

        attachments.forEach(({ url }) => {
            const filename = getFileName(url);
            const extension = getFileExtensionName(url) as keyof typeof FileIcons;

            if (/\.(gif|jpe?g|tiff|png|webp|bmp)(\?.*)?$/i.test(filename)) {
                images.push({ alt: filename, src: url });
            } else {
                const icon = FileIcons[extension] || FileText;
                files.push({ extension, filename, icon, url });
            }
        });

        return [files, images];
    }, [attachments]);

    const messageHasContent = messageBody?.replace(/<.+?>/g, '').trim().length > 0;

    if (!messageHasContent && attachments.length === 0) {
        return null;
    }

    return (
        <li
            className="mb-[15px] flex w-full flex-row justify-start overflow-hidden border-b border-border-secondary last:border-b-0"
            data-testid={testid}
            key={messageId}
        >
            <div className="mr-[7px] w-[46px]">
                {sender.type === 'bidder' ? (
                    <div className="relative flex h-[40px] w-[40px] items-center justify-center rounded-full bg-primary-500 text-center [&_svg]:absolute [&_svg]:bottom-[-4px] [&_svg]:h-[35px] [&_svg]:w-[35px]">
                        <User
                            color="inverse"
                            style="fill"
                        />
                    </div>
                ) : (
                    <div className="flex h-[40px] w-[40px] items-center justify-center border border-border-secondary text-center [&_img]:max-h-[38px] [&_img]:max-w-[38px]">
                        <img
                            alt={sender.name}
                            loading="lazy"
                            src={sender.logoUrl}
                        />
                    </div>
                )}
            </div>
            <div className="w-[calc(100%-46px)]">
                <BodySecondary
                    className="m-0"
                    color="secondary"
                >
                    {sender.name}
                </BodySecondary>
                <div className="hui-text-body-secondary text-text-secondary">{longFormatDate(tsSent)}</div>
                <div
                    className={cn(
                        'whitespace-pre-wrap break-words text-base text-text-primary [&_p]:m-0',
                        messageHasContent ? 'my-[20px]' : 'my-xs'
                    )}
                    dangerouslySetInnerHTML={{
                        __html: messageHasContent ? messageBody : '',
                    }}
                />
                {Boolean(lightboxImages.length) && (
                    <div className={attachmentContainerClass}>
                        <AttachmentLightbox
                            currentImageIndex={currentImageIndex}
                            images={lightboxImages}
                            isOpen={lightboxIsOpen}
                            onClose={() => setLightboxIsOpen(false)}
                            setImageIndex={setCurrentImageIndex}
                        />
                        {lightboxImages.map(({ alt, src }, i) => (
                            <div
                                className="mb-xs mr-xs flex h-[120px] w-[120px] flex-col items-center justify-center overflow-hidden rounded-sm border border-text-secondary bg-surface-100 uppercase no-underline hover:border-primary-400 [&:hover_h6]:text-primary-400 [&:hover_svg]:text-primary-400 [&_h6]:text-text-secondary"
                                key={i}
                            >
                                <img
                                    alt={alt}
                                    className="max-h-[118px] max-w-[118px] cursor-pointer"
                                    onClick={() => {
                                        setCurrentImageIndex(i);
                                        setLightboxIsOpen(true);
                                    }}
                                    src={src}
                                />
                            </div>
                        ))}
                    </div>
                )}
                {Boolean(fileAttachments.length) && (
                    <div className={attachmentContainerClass}>
                        {fileAttachments.map(({ extension, icon: Icon, url }, i) => (
                            <Link
                                aria-label="open file"
                                key={i}
                                target="_blank"
                                href={url}
                            >
                                <div className="mb-xs mr-xs flex h-[120px] w-[120px] flex-col items-center justify-center overflow-hidden rounded-sm border border-text-secondary bg-surface-100 uppercase no-underline hover:border-primary-400 [&:hover_h6]:text-primary-400 [&:hover_svg]:text-primary-400 [&_h6]:text-text-secondary">
                                    <Icon
                                        className="mb-2xs inline-block text-surface-500"
                                        size="md"
                                        style="fill"
                                    />
                                    <H6>{`${extension}`}</H6>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </li>
    );
};

export { Message };
